import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of, NEVER } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { hasItem, isSupported } from '../utils/localstorage';
import { KeycloakService } from './keycloak.service';
import { WEB_ENDPOINT, NATUP_OAUTH_AUTHORIZE_URL, NATUP_OAUTH_CLIENT_ID, API_ENDPOINT } from '../utils/env';

// au moins un doit être là pour tous les providers
const SSO_TOP_PARAMS = ['sso', 'token'];
const SSO_ACTURA_PARAMS = ['token'];
const SSO_UNEAL_PARAMS = ['token'];
const SSO_NATUP_PARAMS = ['code'];

@Injectable({ providedIn: 'root' })
export class SSOService {
  public static logged = false;
  static LOCAL_STORAGE_KEY_QUERY = 'sso-auth-query';
  static LOCAL_STORAGE_KEY_PROVIDER = 'sso-auth-provider';

  public static init(): Observable<any> {
    // Natup SSO: detect ?sso=natup and redirect to Auth0
    const params = SSOService.getQueryParams();
    if (params.get('sso') === 'natup') {
      SSOService.redirectToNatupAuth0();
      return NEVER;
    }

    let ssoQueryString: string | undefined;
    let provider = SSOService.checkProvider(params);
    if (provider) {
      ssoQueryString = SSOService.toSSOQueryString(provider, params);
    } else {
      provider = SSOService.getProviderFromLocalStorage();
      ssoQueryString = SSOService.getSSOParamsFromLocalStorage();

      if (provider === 'natup') {
        console.log('Skipping Natup SSO from localStorage - OAuth codes are single-use');
        SSOService.logout();
        provider = null;
        ssoQueryString = undefined;
      }
    }

    if (provider && ssoQueryString) {
      return SSOService.updateKeycloakToken(provider, ssoQueryString);
    }
    return of(false);
  }

  private static getSSOParamsFromLocalStorage(): string | undefined {
    if (isSupported) {
      if (hasItem(SSOService.LOCAL_STORAGE_KEY_QUERY)) {
        return localStorage.getItem(SSOService.LOCAL_STORAGE_KEY_QUERY)!;
      }
    }
    return undefined;
  }

  private static getProviderFromLocalStorage(): string | null {
    if (isSupported) {
      if (hasItem(SSOService.LOCAL_STORAGE_KEY_PROVIDER)) {
        return localStorage.getItem(SSOService.LOCAL_STORAGE_KEY_PROVIDER);
      }
    }
    return null;
  }

  public static logout() {
    console.log('logout sso');
    if (isSupported) {
      if (hasItem(SSOService.LOCAL_STORAGE_KEY_QUERY)) {
        localStorage.removeItem(SSOService.LOCAL_STORAGE_KEY_QUERY);
      }
      if (hasItem(SSOService.LOCAL_STORAGE_KEY_PROVIDER)) {
        localStorage.removeItem(SSOService.LOCAL_STORAGE_KEY_PROVIDER);
      }
    }
  }

  public static updateKeycloakToken(providerCode: string, ssoQueryString: string): Observable<any> {
    console.log('updateKeycloakToken ' + providerCode);
    const interopServerPath = API_ENDPOINT + '/interop/' + providerCode;

    // Use fetch API directly since we're pre-bootstrap (no Angular DI available yet)
    return new Observable((subscriber) => {
      fetch(interopServerPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: ssoQueryString,
      })
        .then((res) => res.json())
        .then((json) => {
          console.log('login ' + providerCode);
          SSOService.logged = true;
          if (isSupported && providerCode !== 'natup') {
            localStorage.setItem(SSOService.LOCAL_STORAGE_KEY_QUERY, ssoQueryString);
            localStorage.setItem(SSOService.LOCAL_STORAGE_KEY_PROVIDER, providerCode);
          }
          KeycloakService.registerLogoutFunc(SSOService.logout);
          KeycloakService.init(json.data.accessToken, json.data.refreshToken, json.data.idToken).subscribe({
            next: (v) => {
              subscriber.next(v);
              subscriber.complete();
            },
            error: (e) => subscriber.error(e),
          });
        })
        .catch((err) => subscriber.error(err));
    });
  }

  private static toSSOQueryString(provider: string, paramsInQuery: Map<string, string>): string {
    const ssoParams = SSOService.toProviderParams(provider);
    let ssoQueryString = '';
    let isFirst = true;
    for (const ssoParam of ssoParams!) {
      if (!isFirst) {
        ssoQueryString += '&';
      } else {
        isFirst = false;
      }
      ssoQueryString += ssoParam + '=' + paramsInQuery.get(ssoParam);
    }
    return ssoQueryString;
  }

  private static checkProvider(paramsInQuery: Map<string, string>): string | null {
    const interop_prefix = '/interop/';
    if (!window.location.pathname.startsWith(interop_prefix)) {
      return null;
    }

    const provider = window.location.pathname.substring(interop_prefix.length);
    const ssoParams = SSOService.toProviderParams(provider);
    if (!ssoParams) {
      return null;
    }

    for (const ssoParam of ssoParams) {
      if (!paramsInQuery.has(ssoParam)) {
        return null;
      }
    }

    return provider;
  }

  private static getQueryParams(): Map<string, string> {
    const params = new Map<string, string>();
    let query = window.location.search;
    if (query.length < 1) {
      return params;
    } else {
      query = query.substr(1);
    }
    const paramValues = query.split('&');
    for (const paramValue of paramValues) {
      const indexEq = paramValue.indexOf('=');
      if (indexEq !== -1) {
        const key = paramValue.substr(0, indexEq);
        const value = paramValue.substr(indexEq + 1, paramValue.length - indexEq - 1);
        params.set(key, value);
      }
    }
    return params;
  }

  public static toProviderParams(code: string): string[] | null {
    if (code == 'actura') {
      return SSO_ACTURA_PARAMS;
    }
    if (code == 'uneal') {
      return SSO_UNEAL_PARAMS;
    }
    if (code == 'natup') {
      return SSO_NATUP_PARAMS;
    }
    return null;
  }

  public static toProviderString(code: string): string {
    if (code == 'actura') {
      return 'Actura';
    }
    if (code == 'uneal') {
      return 'UNEAL';
    }
    if (code == 'natup') {
      return 'Natup';
    }
    return code;
  }

  private static redirectToNatupAuth0(): void {
    if (!NATUP_OAUTH_AUTHORIZE_URL || !NATUP_OAUTH_CLIENT_ID) {
      console.error('Natup SSO not configured for this environment');
      return;
    }

    const redirectUri = WEB_ENDPOINT + '/interop/natup';
    const state = SSOService.generateRandomState();

    const authUrl =
      NATUP_OAUTH_AUTHORIZE_URL +
      '?client_id=' +
      encodeURIComponent(NATUP_OAUTH_CLIENT_ID) +
      '&redirect_uri=' +
      encodeURIComponent(redirectUri) +
      '&response_type=code' +
      '&scope=' +
      encodeURIComponent('openid profile email') +
      '&state=' +
      encodeURIComponent(state);

    console.log('Redirecting to Natup Auth0: ' + authUrl);
    window.location.href = authUrl;
  }

  private static generateRandomState(): string {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => ('0' + byte.toString(16)).slice(-2)).join('');
  }
}

@Injectable({ providedIn: 'root' })
export class SSOGuard {
  constructor(private router: Router) {}
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    const url = route.url.join('');
    const queryParams = state.root.queryParams;
    if (url === 'home' && this.isFromSSO(queryParams)) {
      this.router.navigate(['/home'], { queryParams: {} });
    }
    return true;
  }

  isFromSSO(paramsInQuery: any): boolean {
    for (const ssoParam of SSO_TOP_PARAMS) {
      if (paramsInQuery.hasOwnProperty(ssoParam)) {
        return true;
      }
    }
    return false;
  }
}
